import { useCallback, useState } from 'react';

import { ButtonLoadingAnimation, Icons, Text, TextButton } from '@opendoor/bricks-next';
import { Box } from '@opendoor/bricks/core';
import { useRouter } from 'next/router';

import {
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { useT } from 'helpers/i18n';

import { gateManualAddressVerification } from '../../components/shared/NewSellerFlowAddressInput';
import { useAddressValidationContext } from '../../helpers/AddressValidationContext';
import AutocompleteInput, { AutocompleteItem } from './AutocompleteInput';

interface PredictionTerm {
  offset: number;
  value: string;
}
export interface AutoCompletePrediction extends AutocompleteItem {
  description: string;
  place_id?: string;
  addressToken?: string;
  types?: string[];
  source: string;
  terms?: PredictionTerm[];
}

export type NovoAddressInputProps = {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  prediction: {
    predictions: AutoCompletePrediction[];
    setPredictions: (predictions: AutoCompletePrediction[]) => void;
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handlePredictionSelect: (prediction: AutoCompletePrediction) => void;
    listBoxId?: string;
  };
  product?: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  pageSetPath?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
};

const ManualEntryButton: React.FC<{
  onMouseDown: (e: React.MouseEvent) => void;
  onPress: () => void;
}> = ({ onMouseDown, onPress }) => {
  const { t } = useT();

  return (
    <TextButton
      zIndex={2}
      onMouseDown={onMouseDown}
      label={t('homepage.titles.address', 'Enter address manually instead')}
      analyticsName="cosmos-address-input-manual-entry"
      type="button"
      onPress={onPress}
      width={'100%'}
      variant="primary"
      size="medium"
      px={20}
      paddingTop={10}
      paddingBottom={20}
    />
  );
};

const NovoAddressInput: React.FC<NovoAddressInputProps> = ({
  id,
  placeholder,
  disabled,
  hideLabel,
  prediction,
  pageSetPath,
  trackingKeywords,
  partnerReferralId,
  channel,
}) => {
  const {
    predictions,
    setPredictions,
    searchTerm,
    onChange,
    handlePredictionSelect,
    listBoxId = 'cb1-listbox',
  } = prediction;
  const [isFocused, setIsFocused] = useState(false);
  const router = useRouter();
  const { isPredictionSelectLoading } = useAddressValidationContext();

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleManualEntryPress = useCallback(() => {
    gateManualAddressVerification({
      forceRedirect: true,
    }).then((result) => {
      if (result.shouldRedirect && result.destination) {
        router.push({
          pathname: result.destination,
          query: {
            ...(trackingKeywords != null && { trackingKeywords }),
            ...(partnerReferralId != null && { partnerReferralId }),
            ...(pageSetPath != null && { pageSetPath }),
            ...(channel != null && { channel }),
          },
        });
      }
    });
  }, [router, trackingKeywords, partnerReferralId, pageSetPath, channel]);

  const handleEscape = useCallback(() => {
    setPredictions([]);
  }, [setPredictions]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const renderItem = (prediction: AutoCompletePrediction) => (
    <Text
      typography="$bodyXlarge"
      tag="span"
      textAlign="left"
      $smallerThanMD={{
        typography: '$bodyMedium',
      }}
      px={10}
      py={2}
    >
      {prediction.description}
    </Text>
  );

  const suffix = !isPredictionSelectLoading ? (
    isFocused ? (
      <Icons.Search color="$contentAccentBrand" />
    ) : (
      <Icons.Search />
    )
  ) : (
    <Box position="relative" px={5}>
      <ButtonLoadingAnimation variant="secondary" size="xsmall" />
    </Box>
  );

  const customAction =
    predictions.length < 5 && searchTerm !== '' && isFocused ? (
      <ManualEntryButton onMouseDown={handleMouseDown} onPress={handleManualEntryPress} />
    ) : undefined;

  return (
    <Box className="od-address-input">
      <Box position={'relative'} zIndex={2} className="cta-input-form">
        <AutocompleteInput<AutoCompletePrediction>
          id={id}
          label="Enter your home address"
          hideLabel={hideLabel}
          placeholder={placeholder}
          disabled={disabled}
          items={predictions}
          searchTerm={searchTerm}
          onSearchChange={onChange}
          onItemSelect={handlePredictionSelect}
          renderItem={renderItem}
          suffix={suffix}
          customAction={customAction}
          listBoxId={listBoxId}
          onEscape={handleEscape}
          onFocus={handleFocus}
          onBlur={handleBlur}
          listBoxAriaLabel="addresses"
        />
      </Box>
    </Box>
  );
};

export default NovoAddressInput;
